import React, { useState, useEffect } from 'react';
import {
    FaFileAlt,
    FaUser,
    FaFilePdf,
    FaFileWord,
    FaDownload,
    FaCheckCircle,
    FaTimesCircle,
    FaHourglassHalf,
    FaEye,
    FaSpinner,
    FaCalendarAlt,
    FaUserTie,
    FaBook,
    FaGraduationCap,
    FaSearch
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';

const AssignedPaper = () => {
    const [assignedPapers, setAssignedPapers] = useState([]);
    const [filteredPapers, setFilteredPapers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPaper, setSelectedPaper] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewComment, setReviewComment] = useState('');
    const [actionType, setActionType] = useState(''); // 'accept_for_review', 'accept', 'reject'
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [toast, setToast] = useState(null);
    const navigate = useNavigate();

    // Status options
    const statusOptions = [
        { value: 'all', label: 'All Papers' },
        { value: 'in_queue', label: 'In Queue' },
        { value: 'in_review', label: 'In Review' },
        { value: 'accepted', label: 'Accepted' },
        { value: 'rejected', label: 'Rejected' }
    ];

    useEffect(() => {
        const currentUser = authService.getCurrentUser();
        const isVerifiedReviewer = authService.isVerifiedReviewer(currentUser);
        const isVerifiedEditorial = authService.isVerifiedEditorial(currentUser);

        // Check if user is verified reviewer or editorial board
        if (!currentUser || (!isVerifiedReviewer && !isVerifiedEditorial)) {
            navigate('/');
            return;
        }

        loadAssignedPapers();
    }, [navigate]);

    const loadAssignedPapers = () => {
        setLoading(true);

        const allPapers = JSON.parse(localStorage.getItem('papers') || '[]');
        const currentUser = authService.getCurrentUser();

        // Filter by professorId
        const professorAssignedPapers = allPapers.filter(paper =>
            paper.assignedTo && paper.assignedTo.professorId === currentUser.id
        );

        // Sort by newest first
        const sortedPapers = professorAssignedPapers.sort((a, b) => {
            const dateA = new Date(a.inQueueAt || a.submittedAt);
            const dateB = new Date(b.inQueueAt || b.submittedAt);
            return dateB - dateA;
        });

        setAssignedPapers(sortedPapers);
        setFilteredPapers(sortedPapers);
        setLoading(false);
    };

    useEffect(() => {
        // Apply filters
        let results = assignedPapers;

        if (statusFilter !== 'all') {
            results = results.filter(paper => paper.status === statusFilter);
        }

        if (searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            results = results.filter(paper =>
                paper.paperTitle.toLowerCase().includes(term) ||
                paper.subject.toLowerCase().includes(term) ||
                paper.major.toLowerCase().includes(term) ||
                (paper.userName && paper.userName.toLowerCase().includes(term))
            );
        }

        setFilteredPapers(results);
    }, [searchTerm, statusFilter, assignedPapers]);

    const getStatusConfig = (status) => {
        switch (status) {
            case 'in_queue':
                return {
                    label: 'In Queue',
                    bgColor: 'bg-orange-100',
                    textColor: 'text-orange-700',
                    icon: FaHourglassHalf,
                    borderColor: 'border-orange-500'
                };
            case 'in_review':
                return {
                    label: 'In Review',
                    bgColor: 'bg-yellow-100',
                    textColor: 'text-yellow-700',
                    icon: FaSpinner,
                    borderColor: 'border-yellow-500'
                };
            case 'accepted':
                return {
                    label: 'Accepted',
                    bgColor: 'bg-green-100',
                    textColor: 'text-green-700',
                    icon: FaCheckCircle,
                    borderColor: 'border-green-500'
                };
            case 'rejected':
                return {
                    label: 'Rejected',
                    bgColor: 'bg-red-100',
                    textColor: 'text-red-700',
                    icon: FaTimesCircle,
                    borderColor: 'border-red-500'
                };
            default:
                return {
                    label: 'Submitted',
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-700',
                    icon: FaHourglassHalf,
                    borderColor: 'border-blue-500'
                };
        }
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return null;
        const extension = fileName.split('.').pop().toLowerCase();
        if (extension === 'pdf') {
            return <FaFilePdf className="text-red-500 text-xl" />;
        } else if (extension === 'doc' || extension === 'docx') {
            return <FaFileWord className="text-blue-500 text-xl" />;
        }
        return <FaFileAlt className="text-gray-500 text-xl" />;
    };

    const isDownloadableFile = (fileName) => {
        if (!fileName) return false;
        const extension = fileName.split('.').pop().toLowerCase();
        return extension === 'pdf' || extension === 'doc' || extension === 'docx';
    };

    const handleDownloadFile = (paper) => {
        if (paper.uploadedFile && paper.uploadedFile.url) {
            try {
                const link = document.createElement('a');
                link.href = paper.uploadedFile.url;
                link.download = paper.uploadedFile.name;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                showToast(`Downloading ${paper.uploadedFile.name}`, 'success');
            } catch (error) {
                console.error('Download error:', error);
                showToast('Could not download file. Please try again.', 'error');
            }
        } else {
            showToast('No file available for download', 'error');
        }
    };

    const handlePreviewClick = (paper) => {
        setSelectedPaper(paper);
        setShowPreviewModal(true);
    };

    const handleActionClick = (paper, action) => {
        setSelectedPaper(paper);
        setActionType(action);
        setReviewComment('');

        if (action === 'accept' || action === 'reject') {
            setShowReviewModal(true);
        } else {
            confirmAction();
        }
    };

    const confirmAction = () => {
        if (!selectedPaper || !actionType) return;

        const allPapers = JSON.parse(localStorage.getItem('papers') || '[]');
        const updatedPapers = allPapers.map(paper => {
            if (paper.id === selectedPaper.id) {
                const updatedPaper = { ...paper };

                switch (actionType) {
                    case 'accept_for_review':
                        updatedPaper.status = 'in_review';
                        updatedPaper.inReviewAt = new Date().toISOString();
                        break;
                    case 'accept':
                        updatedPaper.status = 'accepted';
                        updatedPaper.acceptedAt = new Date().toISOString();
                        updatedPaper.reviewComment = reviewComment;
                        break;
                    case 'reject':
                        updatedPaper.status = 'rejected';
                        updatedPaper.rejectedAt = new Date().toISOString();
                        updatedPaper.reviewComment = reviewComment;
                        break;
                    default:
                        break;
                }

                return updatedPaper;
            }
            return paper;
        });

        localStorage.setItem('papers', JSON.stringify(updatedPapers));
        loadAssignedPapers();

        setShowReviewModal(false);
        setSelectedPaper(null);
        setActionType('');
        setReviewComment('');

        let actionText = '';
        switch (actionType) {
            case 'accept_for_review':
                actionText = 'accepted for review';
                break;
            case 'accept':
                actionText = 'accepted';
                break;
            case 'reject':
                actionText = 'rejected';
                break;
            default:
                actionText = 'processed';
                break;
        }
        showToast(`Paper has been ${actionText} successfully`, 'success');
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getTimeSinceAssignment = (paper) => {
        if (!paper.inQueueAt) return 'N/A';

        const assignedDate = new Date(paper.inQueueAt);
        const now = new Date();
        const diffMs = now - assignedDate;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            return diffHours < 1 ? 'Less than an hour' : `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        }

        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
    };

    return (
        <>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Preview Modal */}
            {showPreviewModal && selectedPaper && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 rounded-t-2xl sticky top-0 z-10">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-2xl font-bold text-white mb-2">Paper Preview</h2>
                                    <p className="text-green-100">{selectedPaper.paperTitle}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowPreviewModal(false);
                                        setSelectedPaper(null);
                                    }}
                                    className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="mb-8">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Paper Content</h3>

                                {selectedPaper.uploadedFile ? (
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                {getFileIcon(selectedPaper.uploadedFile.name)}
                                                <div>
                                                    <p className="font-semibold text-gray-900">{selectedPaper.uploadedFile.name}</p>
                                                    <p className="text-sm text-gray-600">
                                                        Attached File
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDownloadFile(selectedPaper)}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-emerald-500 transition-all"
                                            >
                                                <FaDownload />
                                                Download
                                            </button>
                                        </div>
                                        <div className="text-center py-8">
                                            <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                                            <p className="text-gray-500">Preview not available. Please download the file to view content.</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 bg-gray-50 rounded-xl">
                                        <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500">No content available</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl">
                            <button
                                onClick={() => {
                                    setShowPreviewModal(false);
                                    setSelectedPaper(null);
                                }}
                                className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Review Modal */}
            {showReviewModal && selectedPaper && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
                        <div className="text-center mb-6">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${actionType === 'accept' ? 'bg-green-100' : 'bg-red-100'
                                }`}>
                                {actionType === 'accept' ? (
                                    <FaCheckCircle className="w-8 h-8 text-green-600" />
                                ) : (
                                    <FaTimesCircle className="w-8 h-8 text-red-600" />
                                )}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                {actionType === 'accept' ? 'Accept Paper' : 'Reject Paper'}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {actionType === 'accept'
                                    ? 'Please provide comments for this acceptance:'
                                    : 'Please provide reason for rejection:'}
                            </p>
                            <div className="bg-gray-50 p-3 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-gray-900">{selectedPaper.paperTitle}</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {actionType === 'accept' ? 'Acceptance Comments' : 'Rejection Reason'}
                            </label>
                            <textarea
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                rows="4"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                                placeholder={actionType === 'accept'
                                    ? 'Enter your comments...'
                                    : 'Enter the reason for rejection...'
                                }
                                required
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowReviewModal(false);
                                    setActionType('');
                                    setReviewComment('');
                                }}
                                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAction}
                                className={`flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all ${actionType === 'accept'
                                    ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                                    : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                                    }`}
                            >
                                {actionType === 'accept' ? 'Confirm Acceptance' : 'Confirm Rejection'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-6">
                <div className="max-w mx-auto px-4">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-lg p-6 mb-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Assigned Papers</h1>
                                <p className="text-green-100">Review papers assigned to you</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                    <FaFileAlt className="text-2xl text-white" />
                                </div>
                                <div className="text-right">
                                    <p className="text-white text-sm">Total Assigned</p>
                                    <p className="text-2xl font-bold text-white">{assignedPapers.length}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters and Search */}
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Search Papers</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search by title, subject, author..."
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                                    />
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Status</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                                >
                                    {statusOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <FaSpinner className="text-4xl text-green-500 animate-spin" />
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <p className="text-gray-600">
                                    Showing <span className="font-bold">{filteredPapers.length}</span> of{' '}
                                    <span className="font-bold">{assignedPapers.length}</span> assigned papers
                                </p>
                            </div>

                            {filteredPapers.length === 0 ? (
                                <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                                    <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                        {searchTerm || statusFilter !== 'all' ? 'No Matching Papers' : 'No Assigned Papers'}
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        {searchTerm || statusFilter !== 'all'
                                            ? 'Try adjusting your search or filter criteria'
                                            : 'You have no papers assigned for review yet.'}
                                    </p>
                                    {(searchTerm || statusFilter !== 'all') && (
                                        <button
                                            onClick={clearFilters}
                                            className="mt-4 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-emerald-500 transition-all"
                                        >
                                            Clear Filters
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-6">
                                    {filteredPapers.map((paper) => {
                                        const statusConfig = getStatusConfig(paper.status);
                                        const StatusIcon = statusConfig.icon;

                                        const canAcceptForReview = paper.status === 'in_queue';
                                        const canAcceptOrReject = paper.status === 'in_review';

                                        return (
                                            <div
                                                key={paper.id}
                                                className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border-l-4 ${statusConfig.borderColor}`}
                                            >
                                                <div className="p-6">
                                                    <div className="flex justify-between items-start mb-6">
                                                        <div className="flex items-center gap-4">
                                                            <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${statusConfig.bgColor} ${statusConfig.textColor} text-sm font-semibold`}>
                                                                <StatusIcon className={paper.status === 'in_review' ? 'animate-spin' : ''} />
                                                                {statusConfig.label}
                                                            </div>
                                                            <div className="flex items-center gap-1 text-sm text-gray-500">
                                                                <FaCalendarAlt className="text-xs" />
                                                                <span>Assigned {getTimeSinceAssignment(paper)}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-4 mb-6">
                                                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-xl border-2 border-indigo-200 flex-[2_1_250px] min-w-[250px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaBook className="text-indigo-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Paper Title</p>
                                                            </div>
                                                            <p className="font-bold text-gray-900 text-sm line-clamp-2">{paper.paperTitle}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-4 rounded-xl border-2 border-teal-200 flex-[1_1_150px] min-w-[150px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaBook className="text-teal-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Subject</p>
                                                            </div>
                                                            <p className="font-bold text-gray-900 text-sm">{paper.subject}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border-2 border-amber-200 flex-[1_1_150px] min-w-[150px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaGraduationCap className="text-amber-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Major</p>
                                                            </div>
                                                            <p className="font-bold text-gray-900 text-sm">{paper.major}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border-2 border-blue-200 flex-[1_1_180px] min-w-[180px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaUser className="text-blue-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Author</p>
                                                            </div>
                                                            <p className="font-semibold text-gray-900 text-sm">{paper.userName}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200 flex-[1_1_180px] min-w-[180px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaFileAlt className="text-green-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Content</p>
                                                            </div>
                                                            {paper.uploadedFile ? (
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2">
                                                                        {getFileIcon(paper.uploadedFile.name)}
                                                                        <p className="text-xs font-bold text-gray-900 truncate" title={paper.uploadedFile.name}>
                                                                            {paper.uploadedFile.name}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        {isDownloadableFile(paper.uploadedFile.name) ? (
                                                                            <button
                                                                                onClick={() => handleDownloadFile(paper)}
                                                                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-all"
                                                                            >
                                                                                <FaDownload className="text-xs" />
                                                                                Download
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                onClick={() => handlePreviewClick(paper)}
                                                                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-all"
                                                                            >
                                                                                <FaEye className="text-xs" />
                                                                                Preview
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <p className="text-xs text-gray-400">No content</p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-100">
                                                        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                                                            {paper.submittedAt && (
                                                                <div className="flex items-center gap-1">
                                                                    <FaCalendarAlt />
                                                                    <span>Submitted: {formatDate(paper.submittedAt)}</span>
                                                                </div>
                                                            )}
                                                            {paper.inQueueAt && (
                                                                <div className="flex items-center gap-1">
                                                                    <FaCalendarAlt />
                                                                    <span>Assigned: {formatDate(paper.inQueueAt)}</span>
                                                                </div>
                                                            )}
                                                            {paper.acceptedAt && (
                                                                <div className="flex items-center gap-1">
                                                                    <FaCalendarAlt />
                                                                    <span>Accepted: {formatDate(paper.acceptedAt)}</span>
                                                                </div>
                                                            )}
                                                            {paper.rejectedAt && (
                                                                <div className="flex items-center gap-1">
                                                                    <FaCalendarAlt />
                                                                    <span>Rejected: {formatDate(paper.rejectedAt)}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="flex gap-2">
                                                            {canAcceptForReview && (
                                                                <button
                                                                    onClick={() => handleActionClick(paper, 'accept_for_review')}
                                                                    className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-all font-medium whitespace-nowrap"
                                                                >
                                                                    <FaCheckCircle />
                                                                    Accept for Review
                                                                </button>
                                                            )}

                                                            {canAcceptOrReject && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleActionClick(paper, 'accept')}
                                                                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all font-medium whitespace-nowrap"
                                                                    >
                                                                        <FaCheckCircle />
                                                                        Accept
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleActionClick(paper, 'reject')}
                                                                        className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-medium whitespace-nowrap"
                                                                    >
                                                                        <FaTimesCircle />
                                                                        Reject
                                                                    </button>
                                                                </>
                                                            )}

                                                            {(paper.status === 'accepted' || paper.status === 'rejected') && (
                                                                <div className="px-4 py-2 bg-gray-50 rounded-lg">
                                                                    <p className="text-sm text-gray-600 font-medium whitespace-nowrap">
                                                                        Review completed on {formatDate(paper.acceptedAt || paper.rejectedAt)}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default AssignedPaper;
